/* global Image */
import {
  ChatAttachmentType,
  IChatImageAttachment,
  IChatPdfAttachment,
} from '@engoo/realtime-protocol/chat';
import { makeStreamPosition, StreamPosition } from '@engoo/realtime-protocol/persisted';
import { tFunctionType, useI18n } from 'app/components/localization';
import { useBookingState } from 'app/containers/rtc/BookingContext/BookingState';
import { IRtcControl, useRTC } from 'app/containers/rtc/Provider';
import { Map, Record, Set } from 'immutable';
import React, { useContext } from 'react';
import { PdfCallRoute } from 'app/constants/Routes';
import { isPDF } from 'app/constants/MediaTypes';
import generatePdfPreview from 'app/services/pdf/generatePdfPreview';
import { BookingId } from 'app/models/rtc/booking/constants';
import {
  SetMessageAttachmentAction,
  SetMessageErrorAction,
  StartImageUploadAction,
  useChatContext,
} from 'app/components/chat/ChatDocumentContext';
import { PersistedChatMessages } from 'app/models/chat/reducer';

export interface IChatImageContext {
  upload: ChatImageProvider['initUpload'];
  sendImageError: ChatImageProvider['sendImageError'];
  getImageState: ChatImageProvider['getImageState'];
  finishLoading: ChatImageProvider['finishLoading'];
  retryUpload: ChatImageProvider['retryUpload'];
}

interface IState {
  loaded: Set<number>;
}

const ChatImageContext = React.createContext<IChatImageContext>({
  upload: () => { throw new Error('not implemented'); },
  sendImageError: () => { throw new Error('not implemented'); },
  getImageState: () => { throw new Error('not implemented'); },
  finishLoading: () => { throw new Error('not implemented'); },
  retryUpload: () => { throw new Error('not implemented'); },
});

interface IImageState {
  isLoading: boolean;
  url: string;
  href: string;
  name: string;
  persisted: boolean;
  error: string;
  dimensions?: {
    x: number;
    y: number;
  };
  canRetry: boolean;
}

const ImageState = Record<IImageState>({
  isLoading: true,
  url: '',
  href: '',
  name: '',
  persisted: false,
  error: '',
  dimensions: undefined,
  canRetry: false,
});

const NEW_UPLOAD_STATE = new ImageState({});

interface IPendingImageUpload {
  type: ChatAttachmentType.Image;
  attachment: IChatImageAttachment;
  image: Blob;
}

interface IPendingPdfUpload {
  type: ChatAttachmentType.PDF;
  attachment: IChatPdfAttachment;
  preview: Blob;
  pdf: Blob;
}

type PendingUpload = IPendingImageUpload | IPendingPdfUpload;

class ChatImageProvider extends React.Component<IDeps, IState> {
  // Used to keep file until upload is successful, will otherwise be used for retrying image uploads
  private pendingUploads: Map<number, PendingUpload>;

  public constructor(props: IDeps) {
    super(props);

    this.state = { loaded: Set() };
    this.pendingUploads = Map();
  }

  public componentDidUpdate(prevProps: IDeps) {
    const { bookingId: currentBookingId } = this.props;
    const { bookingId: previousBookingId } = prevProps;

    // Clear the local state if the call ended or the user switched to a different call
    if ((previousBookingId && !currentBookingId) || (previousBookingId !== currentBookingId)) {
      this.setState(({ loaded }) => ({ loaded: Set() }));
      this.pendingUploads = Map();
    }
  }

  public render() {
    const { initUpload: upload, finishLoading, getImageState, sendImageError, retryUpload } = this;
    return (
      <ChatImageContext.Provider value={{ upload, finishLoading, getImageState, sendImageError, retryUpload }} >
        {this.props.children}
      </ChatImageContext.Provider>
    );
  }

  private initUpload = async (file: File) => {
    const { startImageUpload, isBlocked } = this.props;
    if (isBlocked) {
      return;
    }

    const upload = await this.createPendingUpload(file);
    const { position } = await startImageUpload({ attachment: upload.attachment });

    this.pendingUploads = this.pendingUploads.set(position, upload);

    this.uploadHelper(makeStreamPosition(position));
  };

  private sendImageError = (err: Error) => {
    this.props.startImageUpload({ error: err.message });
  };

  private retryUpload = (position: StreamPosition) => {
    const { setMessageError, isBlocked } = this.props;
    if (isBlocked) {
      return;
    }

    if (this.pendingUploads.has(position)) {
      setMessageError(position, '');
      this.uploadHelper(position);
    }
  };

  private finishLoading = (position: number) => {
    this.setState(({ loaded }) => ({ loaded: loaded.add(position) }));
  };

  private getImageState = (position: number) => {
    const message = this.props.persistedMessages.get(position);
    if (message) {
      const attachment = message.attachment;

      const newState = new ImageState({
        persisted: !message.pending,
        error: message.error,
        isLoading: !message.error && !this.state.loaded.has(position),
        canRetry: this.pendingUploads.has(position),
      });

      if (attachment && attachment.type === ChatAttachmentType.Image) {
        return newState.merge({
          url:        attachment.url,
          dimensions: attachment.dimensions,
        });
      } else if (attachment && attachment.type === ChatAttachmentType.PDF) {
        return newState.merge({
          url:        attachment.previewUrl,
          dimensions: attachment.previewDimensions,
          name:       attachment.name,
          href:       PdfCallRoute.getPathname({ position }),
        });
      } else {
        return newState;
      }
    }

    return NEW_UPLOAD_STATE;
  };

  private async uploadHelper(position: StreamPosition) {
    const { uploadChatImage, setMessageAttachment, setMessageError, t } = this.props;
    try {
      const upload = this.pendingUploads.get(position);
      if (!upload) return;

      const update =
        upload.type === ChatAttachmentType.Image ?
        {
          url: (await uploadChatImage(upload.image)).url,
        } :
        upload.type === ChatAttachmentType.PDF ?
        {
          url: (await uploadChatImage(upload.pdf)).url,
          previewUrl: (await uploadChatImage(upload.preview)).url,
        } :
        {};

      setMessageAttachment(position, {
        ...upload.attachment,
        ...update,
      });

      this.pendingUploads = this.pendingUploads.delete(position);
    } catch (e) {
      // Display generic message to user for server error responses
      const errorMessage = t('rtc.errors.file-upload-failed');
      setMessageError(position, errorMessage);
    }
  }

  private async createPendingUpload(file: File): Promise<PendingUpload> {
    if (isPDF(file.type)) {
      const preview = await generatePdfPreview(file);

      return {
        type:       ChatAttachmentType.PDF,
        pdf:        file,
        preview:    preview.blob,
        attachment: {
          type:              ChatAttachmentType.PDF,
          name:              file.name,
          url:               '',
          previewUrl:        '',
          previewDimensions: { x: preview.width, y: preview.height },
        },
      };
    } else {
      return {
        type:       ChatAttachmentType.Image,
        image:      file,
        attachment: {
          type:       ChatAttachmentType.Image,
          url:        '',
          dimensions: await this.getImageDimensions(file),
        },
      };
    }
  }

  private async getImageDimensions(image: File) {
    // Calculate image dimensions and send them with the initial message to eventually assist with the
    // sizing of generic placeholders
    const i = new Image();
    const src = URL.createObjectURL(image);

    try {
      await new Promise((resolve, reject) => {
        i.onload = resolve;
        i.onerror = reject;
        i.src = src;
      });
    } finally {
      URL.revokeObjectURL(src);
    }

    return { x: i.naturalWidth, y: i.naturalHeight };
  }
}

export const useChatImageContext = (): IChatImageContext => (
  useContext(ChatImageContext)
);

// Export non-masqueraded and unconnected version of the provider for use in testing
const BaseChatImageProvider = ChatImageContext.Provider;
export { BaseChatImageProvider };
export { ChatImageProvider as ChatImageProviderUnconnected };
export { ChatImageProviderInjectDeps as ChatImageProvider };
export { ChatImageConsumer };

const ChatImageConsumer: React.FunctionComponent<{
  children: (chatImageService: IChatImageContext) => React.ReactNode,
}> = ({ children }) => (
  <ChatImageContext.Consumer>{
    (chatImageService) => {
      if (chatImageService === undefined) {
        return null;
      }
      return children(chatImageService);
    }
  }</ChatImageContext.Consumer>
);

interface IDeps {
  bookingId: BookingId | undefined;
  t: tFunctionType;
  uploadChatImage: IRtcControl['uploadChatImage'];
  isBlocked: boolean;
  persistedMessages: PersistedChatMessages;
  setMessageAttachment: SetMessageAttachmentAction;
  setMessageError: SetMessageErrorAction;
  startImageUpload: StartImageUploadAction;
}

export type IChatImageProviderDeps = IDeps;

const ChatImageProviderInjectDeps: React.FunctionComponent = ({ children }) => {
  const { t } = useI18n();
  const rtc = useRTC();
  const bookingState = useBookingState();
  const bookingId = bookingState && bookingState.bookingId;

  const {
    isBlocked,
    chatDocumentState: { persisted },
    setMessageAttachment,
    setMessageError,
    startImageUpload,
  } = useChatContext();

  if (!rtc) return null;

  return (
    <ChatImageProvider
      t={t}
      uploadChatImage={rtc.uploadChatImage}
      bookingId={bookingId}
      persistedMessages={persisted}
      setMessageAttachment={setMessageAttachment}
      setMessageError={setMessageError}
      startImageUpload={startImageUpload}
      isBlocked={isBlocked}
    >
      {children}
    </ChatImageProvider>
  );
};
